<template>
  <PanelItem :index="index" :field="field">
    <template #value>
      <div class="tp-chart">
        <ol class="tp-chart__steps">
          <li
            v-for="(step, i) in field.steps || []"
            :key="step.key"
            class="tp-chart__step"
            :class="stepClass(i)"
          >
            <span class="tp-chart__dot">{{ i + 1 }}</span>
            <span>{{ stepLabel(step, i) }}</span>
          </li>
        </ol>

        <div class="tp-chart__next">
          <span class="tp-chart__next-title">{{ label('next', 'Next step') }}:</span>
          {{ field.next_step }}
        </div>

        <div class="tp-chart__toolbar">
          <div class="tp-chart__legend">
            <span><i class="tp-chart__swatch" :style="{ background: colors.zone }" />{{ label('zone', 'Zone') }}</span>
            <span><i class="tp-chart__swatch tp-chart__swatch--dash" :style="{ borderColor: colors.break }" />{{ label('break', 'Break') }}</span>
            <span><i class="tp-chart__swatch" :style="{ background: colors.tp }" />{{ label('take_profit', 'TP') }}</span>
            <span><i class="tp-chart__swatch" :style="{ background: colors.sl }" />{{ label('stop_loss', 'SL') }}</span>
            <span><i class="tp-chart__swatch tp-chart__swatch--dash" :style="{ borderColor: colors.range }" />{{ label('range_high', 'Range high') }} / {{ label('range_low', 'Range low') }}</span>
          </div>
          <div class="tp-chart__intervals">
            <button
              v-for="tf in intervals"
              :key="tf"
              type="button"
              class="tp-chart__tf"
              :class="{ 'tp-chart__tf--active': interval === tf }"
              @click="interval = tf"
            >
              {{ tf }}
            </button>
          </div>
        </div>

        <div ref="wrapper" class="tp-chart__stack" :style="{ height: `${height}px` }">
          <div v-if="loading" class="tp-chart__status">{{ label('loading', 'Loading candles…') }}</div>
          <div v-else-if="error" class="tp-chart__status tp-chart__status--error">{{ error }}</div>
          <div ref="container" class="tp-chart__pane" />
        </div>
      </div>
    </template>
  </PanelItem>
</template>

<script>
import { createChart, ColorType, LineStyle } from 'lightweight-charts'
import { tehranChartClockOptions } from '@modules/heatmap/templates/js/composables/chartTime.js'

const INTERVALS = ['1m', '5m', '15m', '1h']

const SECONDS = { '1m': 60, '5m': 300, '15m': 900, '1h': 3600 }

// Order of the stages, used to light up the step bar.
const STAGE = { watching: 0, touched: 1, broken: 2, entered: 3 }

export default {
  props: ['index', 'resource', 'resourceName', 'resourceId', 'field'],

  data() {
    return {
      intervals: INTERVALS,
      interval: this.field.interval || '5m',
      height: this.field.height || 520,
      loading: false,
      error: '',
      colors: {
        zone: '#f59e0b',
        break: '#fb923c',
        tp: '#3b82f6',
        sl: '#ef4444',
        range: 'rgba(161, 161, 170, 0.55)',
        long: '#22c55e',
        short: '#ef4444',
      },
    }
  },

  computed: {
    /**
     * How many stages are done: finished points light every step up to
     * the one they ended on.
     */
    reached() {
      const status = String(this.field.status || '')
      if (status in STAGE) return STAGE[status]
      if (this.field.entered_at) return 4
      if (this.field.broken_at) return 2
      if (this.field.touched_at) return 1
      return 0
    },

    finished() {
      return !this.field.live
    },

    isWin() {
      return Number(this.field.result_pct) > 0
    },
  },

  mounted() {
    this.initChart()
    this.loadCandles()
    if (this.field.live) {
      this.pollTimer = setInterval(() => this.loadCandles({ silent: true }), this.field.refresh_ms || 60000)
    }
    window.addEventListener('resize', this.onResize)
  },

  beforeUnmount() {
    clearInterval(this.pollTimer)
    window.removeEventListener('resize', this.onResize)
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
  },

  watch: {
    interval() {
      this.loadCandles()
    },
  },

  methods: {
    label(key, fallback) {
      return (this.field.labels && this.field.labels[key]) || fallback
    },

    /**
     * The last step shows the outcome once the point has finished.
     */
    stepLabel(step, i) {
      if (step.key !== 'result' || !this.finished) return step.label
      const pct = this.field.result_pct
      if (pct === null || pct === undefined) return this.field.status_label
      const value = Number(pct)
      return `${this.field.status_label} · ${value > 0 ? '+' : ''}${value.toFixed(2)}%`
    },

    stepClass(i) {
      if (i === 4) {
        if (!this.finished) return ''
        if (this.field.result_pct === null || this.field.result_pct === undefined) return 'tp-chart__step--muted'
        return this.isWin ? 'tp-chart__step--win' : 'tp-chart__step--loss'
      }
      if (i < this.reached || (i === this.reached && this.finished)) return 'tp-chart__step--done'
      if (i === this.reached) return 'tp-chart__step--current'
      return ''
    },

    formatPrice(price) {
      if (!Number.isFinite(price)) return ''
      const decimals = Number.isInteger(this.field.price_decimals) ? this.field.price_decimals : 4
      return Number(price).toFixed(decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
    },

    initChart() {
      if (!this.$refs.container) return
      const clock = tehranChartClockOptions()

      this.chart = createChart(this.$refs.container, {
        width: this.$refs.wrapper?.clientWidth || 800,
        height: this.height,
        layout: { background: { type: ColorType.Solid, color: '#09090b' }, textColor: '#a1a1aa' },
        grid: {
          vertLines: { color: 'rgba(63,63,70,0.45)' },
          horzLines: { color: 'rgba(63,63,70,0.45)' },
        },
        rightPriceScale: { borderColor: 'rgba(63,63,70,0.6)' },
        localization: clock.localization,
        timeScale: { ...clock.timeScale, borderColor: 'rgba(63,63,70,0.6)', timeVisible: true, secondsVisible: false },
        crosshair: { mode: 0 },
      })

      this.series = this.chart.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
      })
      this.priceLines = []
    },

    onResize() {
      if (this.chart && this.$refs.wrapper) {
        this.chart.applyOptions({ width: this.$refs.wrapper.clientWidth })
      }
    },

    line(price, color, width, style, title) {
      if (!(Number(price) > 0)) return
      this.priceLines.push(this.series.createPriceLine({
        price: Number(price),
        color,
        lineWidth: width,
        lineStyle: style,
        axisLabelVisible: true,
        title,
      }))
    },

    applyLevels() {
      for (const line of this.priceLines) this.series.removePriceLine(line)
      this.priceLines = []

      const f = this.field
      const c = this.colors
      this.line(f.range_high, c.range, 1, LineStyle.Dashed, this.label('range_high', 'Range high'))
      this.line(f.range_low, c.range, 1, LineStyle.Dashed, this.label('range_low', 'Range low'))
      this.line(f.stop_loss, c.sl, 2, LineStyle.Solid, `${this.label('stop_loss', 'SL')} ${this.formatPrice(f.stop_loss)}`)
      this.line(f.take_profit, c.tp, 2, LineStyle.Solid, `${this.label('take_profit', 'TP')} ${this.formatPrice(f.take_profit)}`)
      this.line(f.break, c.break, 2, LineStyle.Dashed, `${this.label('break', 'Break')} ${this.formatPrice(f.break)}`)
      this.line(f.zone, c.zone, 3, LineStyle.Solid, `${this.label('zone', 'Zone')} ${this.formatPrice(f.zone)}`)
    },

    snap(unix, candles) {
      const at = Number(unix)
      if (!at || !candles.length) return null
      let best = null
      for (const candle of candles) {
        if (candle.time <= at) best = candle.time
        else break
      }
      return best
    },

    applyMarkers(candles) {
      if (typeof this.series.setMarkers !== 'function') return
      const f = this.field
      const long = f.trade_side === 'long'
      const markers = []
      const add = (unix, marker) => {
        const time = this.snap(unix, candles)
        if (time) markers.push({ time, ...marker })
      }

      add(f.created_at, { position: 'belowBar', color: '#a1a1aa', shape: 'circle', text: this.label('recorded', 'Recorded') })
      add(f.touched_at, { position: long ? 'aboveBar' : 'belowBar', color: this.colors.zone, shape: 'circle', text: this.label('touched', 'Reached') })
      add(f.broken_at, {
        position: long ? 'aboveBar' : 'belowBar',
        color: this.colors.break,
        shape: long ? 'arrowUp' : 'arrowDown',
        text: this.label('broken', 'Broken'),
      })
      add(f.entered_at, {
        position: long ? 'belowBar' : 'aboveBar',
        color: long ? this.colors.long : this.colors.short,
        shape: long ? 'arrowUp' : 'arrowDown',
        text: long ? this.label('entered_long', 'Long') : this.label('entered_short', 'Short'),
      })

      if (f.closed_at) {
        const outcome = {
          profited: [this.colors.tp, this.label('profited', 'TP')],
          stopped: [this.colors.sl, this.label('stopped', 'SL')],
          held: [this.colors.zone, this.label('held', 'Held')],
          expired: ['#a1a1aa', this.label('expired', 'Expired')],
          superseded: ['#a1a1aa', this.label('replaced', 'Replaced')],
        }[f.status] || ['#a1a1aa', f.status_label]
        add(f.closed_at, { position: 'aboveBar', color: outcome[0], shape: 'square', text: outcome[1] })
      }

      // The library requires markers in time order.
      markers.sort((a, b) => a.time - b.time)
      this.series.setMarkers(markers)
    },

    /**
     * Candles from a little before the point was recorded to a little after
     * it finished (or now), capped at 1000 candles.
     */
    range() {
      const step = SECONDS[this.interval] || 300
      const pad = Math.max(3 * 3600, 60 * step)
      const created = Number(this.field.created_at) || Math.floor(Date.now() / 1000)
      const now = Math.floor(Date.now() / 1000)
      const end = this.field.closed_at ? Math.min(now, Number(this.field.closed_at) + pad) : now
      const start = Math.max(created - pad, end - 990 * step)
      return { start, end }
    },

    async loadCandles(options = {}) {
      if (!this.series) return
      if (!this.field.symbol) {
        this.error = 'Missing symbol'
        return
      }
      const silent = options.silent === true
      const seq = (this.seq = (this.seq || 0) + 1)
      if (!silent) this.loading = true
      this.error = ''

      try {
        const { start, end } = this.range()
        const base = (this.field.api_base || '/api/desk').replace(/\/$/, '')
        const url = `${base}/market/${this.field.exchange || 'binance'}/${this.field.symbol}/candles?interval=${this.interval}&limit=1000&start=${start}&end=${end}`
        const res = await fetch(url, { headers: { Accept: 'application/json' } })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.message || `HTTP ${res.status}`)
        }
        const payload = await res.json()
        const candles = (payload.candles || []).map((c) => ({ time: c.t, open: c.o, high: c.h, low: c.l, close: c.c }))
        if (seq !== this.seq) return
        if (!candles.length) throw new Error('No candles returned')

        this.series.setData(candles)
        this.applyLevels()
        this.applyMarkers(candles)
        if (!silent) {
          this.chart.timeScale().fitContent()
          this.chart.timeScale().applyOptions({ rightOffset: 8 })
        }
      } catch (e) {
        if (seq === this.seq && !silent) this.error = e?.message || 'Failed to load candles'
      } finally {
        if (seq === this.seq && !silent) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.tp-chart {
  width: 100%;
  min-width: 0;
}

.tp-chart__steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0 0 0.6rem;
  padding: 0;
  list-style: none;
}

.tp-chart__step {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem 0.25rem 0.35rem;
  border-radius: 999px;
  border: 1px solid rgba(63, 63, 70, 0.9);
  background: #18181b;
  color: #71717a;
  font-size: 0.72rem;
}

.tp-chart__dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  background: #27272a;
  color: #a1a1aa;
  font-size: 0.65rem;
  font-weight: 700;
}

.tp-chart__step--done {
  color: #d4d4d8;
  border-color: #52525b;
}

.tp-chart__step--done .tp-chart__dot {
  background: #52525b;
  color: #fafafa;
}

.tp-chart__step--current {
  color: #fcd34d;
  border-color: #f59e0b;
}

.tp-chart__step--current .tp-chart__dot {
  background: #f59e0b;
  color: #09090b;
}

.tp-chart__step--win {
  color: #86efac;
  border-color: #22c55e;
}

.tp-chart__step--win .tp-chart__dot {
  background: #22c55e;
  color: #09090b;
}

.tp-chart__step--loss {
  color: #fca5a5;
  border-color: #ef4444;
}

.tp-chart__step--loss .tp-chart__dot {
  background: #ef4444;
  color: #09090b;
}

.tp-chart__step--muted {
  color: #a1a1aa;
  border-color: #52525b;
}

.tp-chart__next {
  margin-bottom: 0.75rem;
  padding: 0.45rem 0.7rem;
  border-radius: 0.4rem;
  background: rgba(245, 158, 11, 0.08);
  border-inline-start: 3px solid #f59e0b;
  color: #e4e4e7;
  font-size: 0.8rem;
}

.tp-chart__next-title {
  color: #fcd34d;
  font-weight: 600;
}

.tp-chart__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

.tp-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  font-size: 0.72rem;
  color: #a1a1aa;
}

.tp-chart__swatch {
  display: inline-block;
  width: 0.9rem;
  height: 0.22rem;
  border-radius: 999px;
  margin-inline-end: 0.3rem;
  vertical-align: middle;
}

.tp-chart__swatch--dash {
  height: 0;
  border-top: 2px dashed;
  border-radius: 0;
  background: transparent;
}

.tp-chart__intervals {
  display: flex;
  gap: 0.35rem;
  direction: ltr;
}

.tp-chart__tf {
  border: 1px solid rgba(63, 63, 70, 0.9);
  background: #18181b;
  color: #a1a1aa;
  border-radius: 0.375rem;
  padding: 0.2rem 0.45rem;
  font-size: 0.7rem;
  cursor: pointer;
}

.tp-chart__tf--active {
  background: #27272a;
  color: #fafafa;
  border-color: #52525b;
}

.tp-chart__stack {
  position: relative;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid rgba(63, 63, 70, 0.8);
  background: #09090b;
}

.tp-chart__pane {
  width: 100%;
  height: 100%;
}

.tp-chart__status {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 9, 11, 0.72);
  color: #d4d4d8;
  font-size: 0.85rem;
}

.tp-chart__status--error {
  color: #fca5a5;
}
</style>
